import React, { useEffect, useRef, useState } from "react";
import { Stage, Layer, Rect, Text, Line } from "react-konva";

const AnnotationHeatmap = ({ image, allAnnotations }) => {
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
    naturalWidth: 0,
    naturalHeight: 0,
  });
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    value: 0,
  });

  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0 });

  const maxContainerWidth = 700;
  const paddingX = 40;
  const paddingY = 20;

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const containerWidth = Math.min(
          containerRef.current.clientWidth,
          maxContainerWidth
        );
        setContainerSize({ width: containerWidth });
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!image || !containerSize.width) return;

    const img = new Image();
    img.onload = () => {
      const scale = (containerSize.width - paddingX) / img.naturalWidth;
      setDimensions({
        width: img.naturalWidth * scale,
        height: img.naturalHeight * scale,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
      });
    };
    img.src = image.data;
  }, [image, containerSize]);

  const generateHeatmapData = () => {
    if (!dimensions.width || !allAnnotations) return null;

    const blockSize = 20;
    const scaleX = dimensions.width / dimensions.naturalWidth;
    const scaleY = dimensions.height / dimensions.naturalHeight;

    const blocksWide = Math.ceil(dimensions.width / blockSize);
    const blocksHigh = Math.ceil(dimensions.height / blockSize);
    const densityGrid = Array(blocksHigh)
      .fill()
      .map(() => Array(blocksWide).fill(0));

    allAnnotations.forEach((imageData) => {
      if (!imageData.annotations) return;

      imageData.annotations.forEach((annotation) => {
        const x = annotation.x * scaleX;
        const y = annotation.y * scaleY;
        const width = annotation.width * scaleX;
        const height = annotation.height * scaleY;

        const startX = Math.floor(x / blockSize);
        const endX = Math.floor((x + width) / blockSize);
        const startY = Math.floor(y / blockSize);
        const endY = Math.floor((y + height) / blockSize);

        for (let by = startY; by <= endY; by++) {
          for (let bx = startX; bx <= endX; bx++) {
            if (by >= 0 && by < blocksHigh && bx >= 0 && bx < blocksWide) {
              densityGrid[by][bx]++;
            }
          }
        }
      });
    });

    const maxDensity = Math.max(...densityGrid.flat());

    return { densityGrid, maxDensity, blockSize };
  };

  const stageWidth = dimensions.width + paddingX;
  const stageHeight = dimensions.height + paddingY;
  // scale the whole stage to fit within 700px
  const scaleFactor =
    containerSize.width / stageWidth -
    0.03 * (containerSize.width / stageWidth);
  const heatmapData = generateHeatmapData();

  return (
    <div
      ref={containerRef}
      className="px-4 w-full max-w-[700px] overflow-hidden mx-auto my-12"
      style={{ boxSizing: "border-box" }}
    >
      {dimensions.width > 0 ? (
        <>
          <Stage
            width={stageWidth * scaleFactor}
            height={stageHeight * scaleFactor}
            scaleX={scaleFactor}
            scaleY={scaleFactor}
            className=""
          >
            <Layer>
              <Rect
                x={paddingX}
                y={paddingY}
                width={dimensions.width}
                height={dimensions.height}
                fill="white"
              />

              {heatmapData &&
                heatmapData.densityGrid.map((row, rowIndex) =>
                  row.map((density, colIndex) => {
                    const normalized =
                      heatmapData.maxDensity > 0
                        ? density / heatmapData.maxDensity
                        : 0;
                    const intensity = Math.floor(255 * (1 - normalized * 0.8));
                    const color = `rgb(${intensity}, ${intensity}, ${intensity})`;
                    return (
                      <Rect
                        key={`${rowIndex}-${colIndex}`}
                        x={colIndex * heatmapData.blockSize + paddingX}
                        y={rowIndex * heatmapData.blockSize + paddingY}
                        width={heatmapData.blockSize}
                        height={heatmapData.blockSize}
                        fill={color}
                        stroke="rgba(200, 200, 200, 0.3)"
                        strokeWidth={1}
                        onMouseEnter={(e) => {
                          const stage = e.target.getStage();
                          const mousePos = stage.getPointerPosition();
                          setTooltip({
                            visible: true,
                            x: mousePos.x + 2,
                            y: mousePos.y + 2,
                            value: density,
                          });
                        }}
                        onMouseLeave={() =>
                          setTooltip({ ...tooltip, visible: false })
                        }
                      />
                    );
                  })
                )}
              {tooltip.visible && (
                <Text
                  x={tooltip.x}
                  y={tooltip.y}
                  text={`Density: ${tooltip.value}`}
                  fontSize={12}
                  padding={4}
                  fill="white"
                  stroke="black"
                  strokeWidth={0.5}
                  background="rgba(0, 0, 0, 0.7)"
                />
              )}

              {heatmapData &&
                Array.from({
                  length:
                    Math.ceil(dimensions.width / (heatmapData.blockSize * 5)) +
                    1,
                }).map((_, i) => {
                  const blockX = i * 5;
                  const originalX = Math.round(
                    (blockX * heatmapData.blockSize) /
                      (dimensions.width / dimensions.naturalWidth)
                  );
                  return (
                    <Text
                      key={`x-${i}`}
                      x={blockX * heatmapData.blockSize}
                      y={5}
                      text={originalX.toString()}
                      fontSize={10}
                      align="center"
                      width={heatmapData.blockSize * 4}
                    />
                  );
                })}

              {heatmapData &&
                Array.from({
                  length:
                    Math.ceil(dimensions.height / (heatmapData.blockSize * 5)) +
                    1,
                }).map((_, i) => {
                  const blockY = i * 5;
                  const originalY = Math.round(
                    (blockY * heatmapData.blockSize) /
                      (dimensions.height / dimensions.naturalHeight)
                  );
                  return (
                    <Text
                      key={`y-${i}`}
                      x={10}
                      y={blockY * heatmapData.blockSize + paddingY}
                      text={originalY.toString()}
                      fontSize={10}
                      align="center"
                    />
                  );
                })}

              <Line
                points={[
                  paddingX,
                  paddingY,
                  dimensions.width + paddingX,
                  paddingY,
                ]}
                stroke="black"
                strokeWidth={1}
              />

              <Line
                points={[
                  paddingX,
                  paddingY,
                  paddingX,
                  dimensions.height + paddingY,
                ]}
                stroke="black"
                strokeWidth={1}
              />
            </Layer>
          </Stage>

          <div className="flex justify-center mt-4 mx-auto max-w-full">
            <div className="flex items-center flex-wrap justify-center">
              <span className="mr-2">Less</span>
              {[0, 0.25, 0.5, 0.75, 1].map((val) => (
                <div
                  key={val}
                  className="w-5 h-5 inline-block border border-gray-300"
                  style={{
                    backgroundColor: `rgb(${Math.floor(
                      255 * (1 - val * 0.8)
                    )}, ${Math.floor(255 * (1 - val * 0.8))}, ${Math.floor(
                      255 * (1 - val * 0.8)
                    )})`,
                  }}
                />
              ))}
              <span className="ml-2">More</span>
            </div>
          </div>
        </>
      ) : (
        <div>Loading heatmap...</div>
      )}
    </div>
  );
};

export default AnnotationHeatmap;
